import React, { useState, useEffect } from 'react';
import { removeToken } from './auth';
import { useNavigate } from 'react-router-dom';
import { Button, Card, LoadingState, ErrorState, Badge } from './components';
import { useToast } from './ToastContext';
import { getPositioningAnalyses, getDashboardStats, getProjectsSummary, getCurrentUser } from './api';
import axiosService from './services/axiosService';
import { API_BASE_URL as API_BASE } from './config/api';
import {
  TrendingUp,
  FolderOpen,
  Search,
  Gauge,
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  Info,
  Zap,
  AlertTriangle,
  Clock,
  FileText,
  Sparkles,
  ExternalLink,
  Circle,
  Users,
  BarChart3,
  MousePointerClick,
  UserCheck,
  Activity,
  Code2,
  Palette,
  Globe,
  Shield,
} from 'lucide-react';

// ── Squelette de chargement ──────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="mx-auto w-full px-2 md:px-0 flex flex-col gap-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="h-9 w-64 bg-neutral-200 rounded-lg animate-pulse mb-2"></div>
          <div className="h-5 w-96 bg-neutral-100 rounded-lg animate-pulse"></div>
        </div>
      </div>
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-neutral-50 rounded-lg p-5 border border-neutral-200/50">
              <div className="h-10 w-20 bg-neutral-200 rounded-lg animate-pulse mb-2"></div>
              <div className="h-4 w-32 bg-neutral-100 rounded-lg animate-pulse"></div>
            </div>
          ))}
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {[1, 2].map((i) => (
          <Card key={i}>
            <div className="space-y-3">
              {[1, 2, 3].map((j) => (
                <div key={j} className="h-20 bg-neutral-50 rounded-lg border border-neutral-200/50 animate-pulse"></div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── Composant StatCard réutilisable ──────────────────────────────────────────

function StatCard({ icon: Icon, iconBg, iconColor, value, label, badge, badgeColor }) {
  return (
    <div className="bg-neutral-50 rounded-lg p-5 border border-neutral-200/50 flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 ${iconBg} rounded-lg`}>
          <Icon className={iconColor} size={20} />
        </div>
      </div>
      <div className="text-3xl font-bold text-accent mb-1">{value}</div>
      <div className="text-neutral-600 text-sm font-medium mb-2">{label}</div>
      {badge && <Badge color={badgeColor || 'accent'}>{badge}</Badge>}
    </div>
  );
}

// ── Dashboard Admin ──────────────────────────────────────────────────────────

function DashboardAdmin({ user, navigate }) {
  const [projects, setProjects] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [prospectStats, setProspectStats] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const [summaryData, statsData] = await Promise.all([
          getProjectsSummary().catch(() => []),
          getDashboardStats().catch(() => null),
        ]);

        const enriched = summaryData.map((s) => {
          const score = s.score_global > 0 ? s.score_global : (s.calculated_score > 0 ? s.calculated_score : null);
          return { ...s, finalScore: score };
        });
        setProjects(enriched);

        const withScore = enriched.filter(p => p.finalScore > 0);
        const scoreMoyen = withScore.length ? withScore.reduce((s, p) => s + p.finalScore, 0) / withScore.length : 0;
        setDashboardData({
          stats: {
            total: summaryData.length,
            excellent: withScore.filter(p => p.finalScore >= 90).length,
            bon: withScore.filter(p => p.finalScore >= 50 && p.finalScore < 90).length,
            aAmeliorer: withScore.filter(p => p.finalScore < 50).length,
            scoreMoyen: Math.round(scoreMoyen * 10) / 10,
          },
          recent: [...enriched].sort((a, b) => new Date(b.date_created) - new Date(a.date_created)).slice(0, 5),
        });

        // Prospects stats
        try {
          const ps = await axiosService.get(`${API_BASE}/api/public/admin/prospects/stats`);
          setProspectStats(ps.data);
        } catch {}

        // Users
        try {
          const us = await axiosService.get(`${API_BASE}/api/public/admin/users`);
          setUsersData(us.data);
        } catch {}
      } catch (e) {
        showToast('Erreur de chargement', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [showToast]);

  if (loading) return <DashboardSkeleton />;

  const usersByType = {
    admin: usersData.filter(u => u.user_type === 'admin').length,
    developer: usersData.filter(u => u.user_type === 'developer').length,
    designer: usersData.filter(u => u.user_type === 'designer').length,
    growthMarketing: usersData.filter(u => u.user_type === 'growth_marketing').length,
    commercial: usersData.filter(u => u.user_type === 'commercial').length,
  };

  return (
    <div className="mx-auto w-full px-2 md:px-0 flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield size={20} className="text-accent" />
            <span className="text-xs font-semibold text-accent uppercase tracking-wide">Administration</span>
          </div>
          <h1 className="font-heading text-3xl font-bold text-neutral-900 mb-1">
            Bonjour, {user?.first_name} !
          </h1>
          <p className="text-blue-gray-500 text-sm">Vue d'ensemble globale de la plateforme.</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => navigate('/projects/create')} variant="primary">Nouveau projet</Button>
          <Button onClick={() => { removeToken(); navigate('/login'); }} variant="secondary">Déconnexion</Button>
        </div>
      </div>

      {/* Stats projets */}
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-accent/10 rounded-lg"><TrendingUp className="text-accent" size={20} /></div>
          <div>
            <div className="font-bold text-neutral-900">Projets SEO</div>
            <div className="text-neutral-500 text-xs">Performance globale</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={FolderOpen} iconBg="bg-accent/10" iconColor="text-accent" value={dashboardData?.stats.total} label="Total projets" />
          <StatCard icon={CheckCircle2} iconBg="bg-green-100" iconColor="text-green-600" value={dashboardData?.stats.excellent} label="Excellents ≥80" badge="≥ 80" badgeColor="success" />
          <StatCard icon={TrendingUp} iconBg="bg-blue-100" iconColor="text-blue-600" value={dashboardData?.stats.bon} label="Bons 50-79" badge="50-79" badgeColor="accent" />
          <StatCard icon={AlertTriangle} iconBg="bg-red-100" iconColor="text-red-500" value={dashboardData?.stats.aAmeliorer} label="À améliorer <50" badge="< 50" badgeColor="error" />
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Utilisateurs */}
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg"><Users className="text-purple-600" size={18} /></div>
            <div>
              <div className="font-bold text-neutral-900 text-sm">Utilisateurs</div>
              <div className="text-neutral-500 text-xs">{usersData.length} compte{usersData.length > 1 ? 's' : ''}</div>
            </div>
            <button onClick={() => navigate('/admin/users')} className="ml-auto text-xs text-accent hover:underline">Gérer →</button>
          </div>
          <div className="space-y-2">
            {[
              { label: 'Admins', count: usersByType.admin, color: 'bg-accent/10 text-accent', icon: Shield },
              { label: 'Développeurs', count: usersByType.developer, color: 'bg-blue-100 text-blue-600', icon: Code2 },
              { label: 'Designers', count: usersByType.designer, color: 'bg-purple-100 text-purple-700', icon: Palette },
              { label: 'Growth Marketing', count: usersByType.growthMarketing, color: 'bg-emerald-100 text-emerald-700', icon: Sparkles },
              { label: 'Commerciaux', count: usersByType.commercial, color: 'bg-green-100 text-green-600', icon: UserCheck },
            ].map(({ label, count, color, icon: Icon }) => (
              <div key={label} className="flex items-center justify-between p-2.5 rounded-lg bg-neutral-50 border border-neutral-100">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${color}`}><Icon size={12} /></span>
                  <span className="text-sm text-neutral-700">{label}</span>
                </div>
                <span className="font-bold text-neutral-900 text-sm">{count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Prospects */}
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-orange-100 rounded-lg"><BarChart3 className="text-orange-500" size={18} /></div>
            <div>
              <div className="font-bold text-neutral-900 text-sm">Prospects & Leads</div>
              <div className="text-neutral-500 text-xs">7 derniers jours</div>
            </div>
            <button onClick={() => navigate('/prospects')} className="ml-auto text-xs text-accent hover:underline">Voir →</button>
          </div>
          {prospectStats ? (
            <div className="space-y-2">
              {[
                { label: 'Audits lancés', value: prospectStats.total_audits ?? '—' },
                { label: 'Taux engagement', value: prospectStats.engagement_rate != null ? `${prospectStats.engagement_rate}%` : '—' },
                { label: 'Note moyenne', value: prospectStats.avg_score_global != null ? `${Math.round(prospectStats.avg_score_global)}/100` : '—' },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between p-2.5 rounded-lg bg-neutral-50 border border-neutral-100">
                  <span className="text-sm text-neutral-600">{label}</span>
                  <span className="font-bold text-neutral-900 text-sm">{value}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-neutral-400 text-center py-4">Données non disponibles</p>
          )}
        </Card>

        {/* Projets récents */}
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-accent/10 rounded-lg"><Zap className="text-accent" size={18} /></div>
            <div>
              <div className="font-bold text-neutral-900 text-sm">Projets récents</div>
              <div className="text-neutral-500 text-xs">Dernières activités</div>
            </div>
          </div>
          <div className="space-y-2">
            {dashboardData?.recent.length ? dashboardData.recent.map(p => (
              <div key={p.id} onClick={() => navigate(`/projects/${p.slug}`)}
                className="flex items-center gap-3 p-2.5 rounded-lg bg-neutral-50 border border-neutral-100 hover:border-accent/40 cursor-pointer transition">
                {p.favicon_url && <img src={p.favicon_url} alt="" className="w-6 h-6 rounded" onError={e => e.target.style.display = 'none'} />}
                <span className="flex-1 text-sm text-neutral-700 truncate">{p.name}</span>
                {p.finalScore > 0 && (
                  <Badge color={p.finalScore >= 90 ? 'success' : p.finalScore >= 50 ? 'accent' : 'error'} className="text-[10px]">
                    {Math.round(p.finalScore)}
                  </Badge>
                )}
              </div>
            )) : <p className="text-xs text-neutral-400 text-center py-4">Aucun projet</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ── Dashboard Développeur ────────────────────────────────────────────────────

function DashboardDeveloper({ user, navigate }) {
  const [projects, setProjects] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  const seoTips = [
    { icon: Search, title: 'Optimisez vos balises Title', desc: 'Titres uniques de 50-60 caractères avec mots-clés.', iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
    { icon: FileText, title: 'Meta descriptions', desc: '150-160 caractères pour améliorer votre CTR.', iconBg: 'bg-green-100', iconColor: 'text-green-600' },
    { icon: Gauge, title: 'Vitesse de chargement', desc: 'Optimisez images et scripts pour vos Core Web Vitals.', iconBg: 'bg-orange-100', iconColor: 'text-orange-600' },
    { icon: Sparkles, title: 'Données structurées', desc: 'Schema.org pour aider Google et afficher des rich snippets.', iconBg: 'bg-purple-100', iconColor: 'text-purple-600' },
  ];

  useEffect(() => {
    (async () => {
      try {
        const summaryData = await getProjectsSummary().catch(() => []);
        const enriched = summaryData.map((s) => {
          const score = s.score_global > 0 ? s.score_global : (s.calculated_score > 0 ? s.calculated_score : null);
          return { ...s, finalScore: score };
        });
        setProjects(enriched);
        const withScore = enriched.filter(p => p.finalScore > 0);
        const scoreMoyen = withScore.length ? withScore.reduce((s, p) => s + p.finalScore, 0) / withScore.length : 0;

        // Todos
        const todosPromises = summaryData.slice(0, 3).map(async (p) => {
          try {
            const pa = await getPositioningAnalyses(p.slug).catch(() => []);
            const todos = (pa?.[0]?.todo_list || []);
            return todos.map(t => {
              const text = typeof t === 'string' ? t : (t.tache || t.title || t.description || '');
              return text ? { id: `${p.slug}-${Math.random()}`, text, projectName: p.name, projectSlug: p.slug } : null;
            }).filter(Boolean);
          } catch { return []; }
        });
        const allTodos = (await Promise.all(todosPromises)).flat().slice(0, 8);

        setDashboardData({
          stats: { total: summaryData.length, scoreMoyen: Math.round(scoreMoyen * 10) / 10, excellent: withScore.filter(p => p.finalScore >= 90).length, aAmeliorer: withScore.filter(p => p.finalScore < 50).length },
          recent: [...enriched].sort((a, b) => new Date(b.date_created) - new Date(a.date_created)).slice(0, 5),
          todos: allTodos,
        });
      } catch (e) { showToast('Erreur de chargement', 'error'); }
      finally { setLoading(false); }
    })();
  }, [showToast]);

  if (loading) return <DashboardSkeleton />;

  return (
    <div className="mx-auto w-full px-2 md:px-0 flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Code2 size={16} className="text-blue-500" />
            <span className="text-xs font-semibold text-blue-500 uppercase tracking-wide">Développeur</span>
          </div>
          <h1 className="font-heading text-3xl font-bold text-neutral-900 mb-1">
            Bonjour, {user?.first_name} !
          </h1>
          <p className="text-blue-gray-500 text-sm">
            {projects.length === 0 ? 'Commencez par créer votre premier projet.' : `${projects.length} projet${projects.length > 1 ? 's' : ''} en cours.`}
          </p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => navigate('/projects/create')} variant="primary">Nouveau projet</Button>
          <Button onClick={() => { removeToken(); navigate('/login'); }} variant="secondary">Déconnexion</Button>
        </div>
      </div>

      {projects.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <FolderOpen size={40} className="mx-auto text-neutral-300 mb-4" />
            <h2 className="text-lg font-semibold text-neutral-800 mb-2">Aucun projet</h2>
            <p className="text-blue-gray-500 mb-4 text-sm">Créez votre premier projet d'analyse SEO.</p>
            <Button onClick={() => navigate('/projects/create')} variant="primary">Créer un projet</Button>
          </div>
        </Card>
      ) : (
        <>
          {/* Stats */}
          <Card>
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-accent/10 rounded-lg"><TrendingUp className="text-accent" size={20} /></div>
              <div className="font-bold text-neutral-900">Vue d'ensemble</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={FolderOpen} iconBg="bg-accent/10" iconColor="text-accent" value={dashboardData?.stats.total} label="Projets" />
              <StatCard icon={TrendingUp} iconBg="bg-accent/10" iconColor="text-accent"
                value={dashboardData?.stats.scoreMoyen ? `${dashboardData.stats.scoreMoyen}/100` : '—'} label="Score moyen" />
              <StatCard icon={CheckCircle2} iconBg="bg-green-100" iconColor="text-green-600" value={dashboardData?.stats.excellent} label="Excellents" badge="≥ 80" badgeColor="success" />
              <StatCard icon={AlertTriangle} iconBg="bg-red-100" iconColor="text-red-500" value={dashboardData?.stats.aAmeliorer} label="À améliorer" badge="< 50" badgeColor="error" />
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Projets récents */}
            <Card>
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-accent/10 rounded-lg"><Zap className="text-accent" size={20} /></div>
                <div><div className="font-bold text-neutral-900">Projets récents</div><div className="text-neutral-500 text-xs">Accès rapide</div></div>
              </div>
              <div className="space-y-2">
                {dashboardData?.recent.map(p => (
                  <div key={p.id} onClick={() => navigate(`/projects/${p.slug}`)}
                    className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200/50 hover:border-accent/40 cursor-pointer transition group">
                    {p.favicon_url && <img src={p.favicon_url} alt="" className="w-8 h-8 rounded" onError={e => e.target.style.display = 'none'} />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-neutral-800 truncate text-sm">{p.name}</div>
                      <div className="text-xs text-neutral-400 flex items-center gap-1"><Clock size={10} />{new Date(p.date_created).toLocaleDateString('fr-FR')}</div>
                    </div>
                    {p.finalScore > 0 ? <Badge color={p.finalScore >= 90 ? 'success' : p.finalScore >= 50 ? 'accent' : 'error'} className="text-[10px]">{Math.round(p.finalScore)}/100</Badge> : <Badge color="gray" className="text-[10px]">Non audité</Badge>}
                    <ArrowRight size={14} className="text-neutral-400 group-hover:text-accent transition" />
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-neutral-200">
                <Button onClick={() => navigate('/projects/create')} variant="primary" className="w-full">Nouveau projet</Button>
              </div>
            </Card>

            {/* Actions prioritaires */}
            <Card>
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-accent/10 rounded-lg"><CheckCircle2 className="text-accent" size={20} /></div>
                <div><div className="font-bold text-neutral-900">Actions prioritaires</div><div className="text-neutral-500 text-xs">Recommandations IA</div></div>
              </div>
              {dashboardData?.todos.length > 0 ? (
                <div className="space-y-2">
                  {dashboardData.todos.slice(0, 6).map(t => (
                    <div key={t.id} onClick={() => navigate(`/projects/${t.projectSlug}/position`)}
                      className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200/50 hover:border-accent/40 cursor-pointer transition">
                      <Circle size={14} className="text-neutral-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-neutral-700 line-clamp-2">{t.text}</div>
                        <div className="text-xs text-neutral-400 mt-1 flex items-center gap-1"><FolderOpen size={10} />{t.projectName}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-neutral-400">
                  <CheckCircle2 size={28} className="mx-auto mb-2 text-neutral-300" />
                  <p className="text-sm">Aucune action pour le moment</p>
                  <p className="text-xs mt-1">Lancez des analyses pour obtenir des recommandations</p>
                </div>
              )}
            </Card>
          </div>

          {/* Conseils SEO */}
          <Card>
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-accent/10 rounded-lg"><Lightbulb className="text-accent" size={20} /></div>
              <div><div className="font-bold text-neutral-900">Conseils SEO</div><div className="text-neutral-500 text-xs">Bonnes pratiques</div></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {seoTips.map((tip, idx) => (
                <div key={idx} className="p-4 bg-neutral-50 rounded-lg border border-neutral-200/50 hover:border-accent/30 transition">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 ${tip.iconBg} rounded-lg flex-shrink-0`}><tip.icon className={tip.iconColor} size={18} /></div>
                    <div>
                      <div className="font-semibold text-neutral-900 mb-1 text-sm">{tip.title}</div>
                      <p className="text-xs text-neutral-600 leading-relaxed">{tip.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ── Dashboard Commercial ─────────────────────────────────────────────────────

function DashboardCommercial({ user, navigate }) {
  const [prospectStats, setProspectStats] = useState(null);
  const [recentAudits, setRecentAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, prospectsRes] = await Promise.all([
          axiosService.get(`${API_BASE}/api/public/admin/prospects/stats`).catch(() => null),
          axiosService.get(`${API_BASE}/api/public/admin/prospects`).catch(() => null),
        ]);
        if (statsRes) setProspectStats(statsRes.data);
        if (prospectsRes) {
          setRecentAudits((prospectsRes.data.items || []).slice(0, 6));
        }
      } catch (e) { showToast('Erreur de chargement', 'error'); }
      finally { setLoading(false); }
    })();
  }, [showToast]);

  if (loading) return <DashboardSkeleton />;

  const totalAudits = prospectStats?.total_audits ?? 0;
  const engagementRate = prospectStats?.engagement_rate ?? 0;
  const avgScore = prospectStats?.avg_score_global != null ? Math.round(prospectStats.avg_score_global) : null;
  const engagedAudits = prospectStats?.engaged_audits ?? 0;

  return (
    <div className="mx-auto w-full px-2 md:px-0 flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <UserCheck size={16} className="text-green-500" />
            <span className="text-xs font-semibold text-green-500 uppercase tracking-wide">Commercial</span>
          </div>
          <h1 className="font-heading text-3xl font-bold text-neutral-900 mb-1">
            Bonjour, {user?.first_name} !
          </h1>
          <p className="text-blue-gray-500 text-sm">Suivez vos prospects et opportunités de conversion.</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => navigate('/prospects')} variant="primary">Voir les prospects</Button>
          <Button onClick={() => { removeToken(); navigate('/login'); }} variant="secondary">Déconnexion</Button>
        </div>
      </div>

      {/* KPIs */}
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-green-100 rounded-lg"><BarChart3 className="text-green-600" size={20} /></div>
          <div>
            <div className="font-bold text-neutral-900">Performance prospects</div>
            <div className="text-neutral-500 text-xs">7 derniers jours</div>
          </div>
          <button onClick={() => navigate('/prospects')} className="ml-auto text-xs text-accent hover:underline flex items-center gap-1">
            Voir tout <ArrowRight size={12} />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Globe} iconBg="bg-blue-100" iconColor="text-blue-600" value={totalAudits} label="Audits lancés" />
          <StatCard icon={MousePointerClick} iconBg="bg-green-100" iconColor="text-green-600" value={engagedAudits} label="Engagés" badge={`${engagementRate}%`} badgeColor="success" />
          <StatCard icon={Activity} iconBg="bg-accent/10" iconColor="text-accent" value={avgScore != null ? `${avgScore}/100` : '—'} label="Note moyenne" />
          <StatCard icon={TrendingUp} iconBg="bg-orange-100" iconColor="text-orange-500" value={`${engagementRate}%`} label="Taux engagement" />
        </div>
      </Card>

      {/* Derniers prospects */}
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-green-100 rounded-lg"><Users className="text-green-600" size={20} /></div>
          <div>
            <div className="font-bold text-neutral-900">Derniers prospects</div>
            <div className="text-neutral-500 text-xs">Audits récents à traiter</div>
          </div>
          <button onClick={() => navigate('/prospects')} className="ml-auto text-xs text-accent hover:underline">Gérer →</button>
        </div>
        {recentAudits.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-100">
                  <th className="text-left font-semibold text-neutral-500 py-2 px-3 text-xs">Site</th>
                  <th className="text-left font-semibold text-neutral-500 py-2 px-3 text-xs">Email</th>
                  <th className="text-left font-semibold text-neutral-500 py-2 px-3 text-xs">Secteur</th>
                  <th className="text-center font-semibold text-neutral-500 py-2 px-3 text-xs">Score</th>
                  <th className="text-left font-semibold text-neutral-500 py-2 px-3 text-xs">Date</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-50">
                {recentAudits.map(a => {
                  const score = a.performance_score || a.seo_score;
                  return (
                    <tr key={a.token} className="hover:bg-neutral-50 transition">
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          {a.favicon_url && <img src={a.favicon_url} alt="" className="w-5 h-5 rounded" onError={e => e.target.style.display = 'none'} />}
                          <span className="font-medium text-neutral-800 truncate max-w-[120px]">{a.url?.replace(/^https?:\/\//, '') || '—'}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-neutral-600 text-xs">{a.email || '—'}</td>
                      <td className="py-2.5 px-3 text-neutral-500 text-xs truncate max-w-[100px]">{a.secteur_activite || '—'}</td>
                      <td className="py-2.5 px-3 text-center">
                        {score > 0 ? (
                          <Badge color={score >= 90 ? 'success' : score >= 50 ? 'accent' : 'error'} className="text-[10px]">{score}</Badge>
                        ) : <span className="text-neutral-300 text-xs">—</span>}
                      </td>
                      <td className="py-2.5 px-3 text-neutral-400 text-xs">
                        {a.created_at ? new Date(a.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—'}
                      </td>
                      <td className="py-2.5 px-3">
                        <button onClick={() => navigate('/prospects')} className="p-1 text-neutral-400 hover:text-accent transition rounded">
                          <ArrowRight size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10 text-neutral-400">
            <Users size={28} className="mx-auto mb-2 text-neutral-200" />
            <p className="text-sm">Aucun prospect pour le moment</p>
          </div>
        )}
      </Card>

      {/* Guide commercial */}
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-green-100 rounded-lg"><Lightbulb className="text-green-600" size={20} /></div>
          <div><div className="font-bold text-neutral-900">Conseils de conversion</div><div className="text-neutral-500 text-xs">Optimisez votre suivi commercial</div></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: MousePointerClick, color: 'bg-blue-100 text-blue-600', title: 'Contacter rapidement', desc: 'Relancez dans les 24h après un audit terminé pour maximiser le taux de conversion.' },
            { icon: Search, color: 'bg-purple-100 text-purple-600', title: 'Analyser le secteur', desc: 'Utilisez le secteur d\'activité détecté pour personnaliser votre approche commerciale.' },
            { icon: TrendingUp, color: 'bg-green-100 text-green-600', title: 'Suivre les engagés', desc: 'Les prospects ayant interagi avec les résultats sont les plus chauds à contacter.' },
          ].map((tip, i) => (
            <div key={i} className="p-4 bg-neutral-50 rounded-lg border border-neutral-100">
              <div className="flex items-start gap-3">
                <div className={`p-2 ${tip.color.split(' ')[0]} rounded-lg flex-shrink-0`}><tip.icon className={tip.color.split(' ')[1]} size={16} /></div>
                <div>
                  <div className="font-semibold text-neutral-800 text-sm mb-1">{tip.title}</div>
                  <p className="text-xs text-neutral-500 leading-relaxed">{tip.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── Composant principal Dashboard ────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  useEffect(() => {
    getCurrentUser()
      .then(u => setUser(u))
      .catch(() => {})
      .finally(() => setUserLoading(false));
  }, []);

  if (userLoading) return <DashboardSkeleton />;

  const userType = user?.user_type || 'developer';

  if (userType === 'admin') return <DashboardAdmin user={user} navigate={navigate} />;
  if (userType === 'commercial') return <DashboardCommercial user={user} navigate={navigate} />;
  return <DashboardDeveloper user={user} navigate={navigate} />;
}
